import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Menu, X, Phone, Mail } from "lucide-react";
import { useState, useEffect } from "react";

const LOGO_URL = "/brand/24k-logo.jpg";
const CONTACT_PHONE_DISPLAY = "(904) 672-9966";
const CONTACT_PHONE_TEL = "+19046729966";
const CONTACT_EMAIL = "glowsupport@monthlyglowonthego.com";

export default function Navbar({ transparent = true }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { href: "/#plans", label: "Plans" },
        { href: "/#services", label: "Services" },
        { href: "/facials", label: "Facials & Makeup" },
        { href: "/#how", label: "How it works" },
    ];

    const handleNav = (href) => (e) => {
        e.preventDefault();
        setOpen(false);
        if (href.startsWith("/#")) {
            const id = href.slice(2);
            if (location.pathname === "/") {
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
            } else {
                navigate("/", { state: { scrollTo: id } });
                setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 220);
            }
        } else {
            navigate(href);
        }
    };

    return (
        <nav
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
                scrolled || !transparent ? "bg-ink/80 backdrop-blur-xl border-b border-hairline" : "bg-transparent"
            }`}
        >
            {/* Top contact bar */}
            <div
                data-testid="nav-contact-bar"
                className={`hidden md:block border-b border-hairline transition-opacity duration-300 ${
                    scrolled ? "opacity-0 h-0 overflow-hidden pointer-events-none" : "opacity-100 bg-ink/60"
                }`}
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 flex items-center justify-end gap-6 h-9 text-[11px] tracking-[0.18em] uppercase text-white/70">
                    <a
                        href={`tel:${CONTACT_PHONE_TEL}`}
                        data-testid="nav-contact-phone"
                        className="flex items-center gap-2 hover:text-rose transition"
                    >
                        <Phone className="w-3 h-3 text-rose" />
                        {CONTACT_PHONE_DISPLAY}
                    </a>
                    <span className="text-white/20">|</span>
                    <a
                        href={`mailto:${CONTACT_EMAIL}`}
                        data-testid="nav-contact-email"
                        className="hidden lg:flex items-center gap-2 hover:text-rose transition normal-case tracking-normal text-xs"
                    >
                        <Mail className="w-3 h-3 text-rose" />
                        {CONTACT_EMAIL}
                    </a>
                </div>
            </div>
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 flex items-center justify-between h-20">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
                    <img
                        src={LOGO_URL}
                        alt="24K Glow On The Go"
                        className="w-10 h-10 rounded-full object-cover ring-1 ring-gold/40 group-hover:ring-gold transition"
                    />
                    <span className="font-serif text-base sm:text-lg tracking-[0.18em] uppercase">
                        <span className="text-gold">24K</span>{" "}
                        <span className="text-white/95">Glow</span>
                    </span>
                </Link>

                <div className="hidden lg:flex items-center gap-10">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            onClick={handleNav(l.href)}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="text-sm tracking-wide text-white/70 hover:text-rose transition cursor-pointer"
                        >
                            {l.label}
                        </a>
                    ))}
                </div>

                <div className="hidden lg:flex items-center gap-3">
                    {user ? (
                        <Link
                            to="/portal"
                            data-testid="nav-portal-btn"
                            className="px-5 py-2 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition"
                        >
                            My Portal
                        </Link>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                data-testid="nav-signin-btn"
                                className="px-4 py-2 text-sm text-white/80 hover:text-white transition"
                            >
                                Sign in
                            </Link>
                            <a
                                href="#plans"
                                data-testid="nav-cta-btn"
                                className="px-5 py-2 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition"
                            >
                                Subscribe
                            </a>
                        </>
                    )}
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    className="lg:hidden p-2"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-hairline bg-ink/95 backdrop-blur-xl">
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={handleNav(l.href)}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-white/80 py-2 cursor-pointer"
                            >
                                {l.label}
                            </a>
                        ))}
                        {user ? (
                            <Link
                                to="/portal"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-portal-btn"
                                className="mt-2 px-5 py-3 rounded-full bg-rose text-ink font-medium text-center"
                            >
                                My Portal
                            </Link>
                        ) : (
                            <Link
                                to="/login"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-signin-btn"
                                className="mt-2 px-5 py-3 rounded-full bg-rose text-ink font-medium text-center"
                            >
                                Sign in
                            </Link>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
}
